import { ndjsonParse } from "@redotech/json/ndjson";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { streamAsyncIterator } from "@redotech/streams-util/async-iterator";
import { sleep } from "@redotech/util/schedule";
import { REDO_MERCHANT_SERVER_URL } from "../config";

// RPC-based team notifications stream using Redis
export async function* getTeamNotificationsStreamRpc({
  rpcClient,
  signal,
  currentId,
}: {
  rpcClient: RedoMerchantRpcClient;
  signal: AbortSignal;
  currentId?: string;
}) {
  try {
    const streamGenerator = rpcClient.watchTeamEvents(
      { currentId },
      { signal },
    );

    for await (const message of streamGenerator) {
      if (signal.aborted) {
        break;
      }
      yield message;
    }
  } catch (error) {
    // Handle AbortError gracefully
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }
    throw error;
  }
}

export const getConnectedTeamsCountsStream = ({
  authorization,
  signal,
}: {
  authorization: { Authorization: string };
  signal: AbortSignal;
}) => {
  const url = `${REDO_MERCHANT_SERVER_URL}/team/conversations/connectedTeamsCountsLive`;
  return fetch(url, { signal, headers: authorization });
};

export const getUserNotificationsStream = ({
  authorization,
  signal,
}: {
  authorization: { Authorization: string };
  signal: AbortSignal;
}) => {
  // We're not using the Math.random() trick here because we're only listening to this stream in one place.
  // If we later listen in two places, we might need to use the trick.
  const url = `${REDO_MERCHANT_SERVER_URL}/user/notifications/live`;
  return fetch(url, { signal, headers: authorization });
};

export async function* listen({
  query,
  loopCondition,
  setErrorMessage,
  setShowErrorMessage,
}: {
  query(): Promise<Response>;
  loopCondition?: boolean;
  setErrorMessage?(message: string): void;
  setShowErrorMessage?(show: boolean): void;
}) {
  while (loopCondition) {
    try {
      const response = await query();

      if (response) {
        // If we get a 400 error, show an error
        if (
          response.status.toString()[0] === "4" &&
          setErrorMessage &&
          setShowErrorMessage
        ) {
          setErrorMessage(
            "Failed to get message updates. Please reload the page. If the issue persists, please contact customer support.",
          );
          setShowErrorMessage(true);
        }
        const data: ReadableStream<any> | null = response.body;
        if (data) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const reader = data!.pipeThrough(new TextDecoderStream());
          for await (const result of ndjsonParse(streamAsyncIterator(reader))) {
            if (result) {
              yield result;
            }
          }
        }
      }
    } catch (error) {
      // Handle AbortError gracefully - stop the loop without throwing
      if (error instanceof Error && error.name === "AbortError") {
        break;
      }
      // Re-throw other errors to be handled by calling code
      throw error;
    }
    await sleep(Temporal.Duration.from({ seconds: 5 }));
  }
}
